import { useEffect, useRef, useState } from "react";
import { Utensils, Gem, Coffee, Briefcase, Scissors, Sparkles } from "lucide-react";

const ICONS = [Utensils, Gem, Coffee, Briefcase, Scissors];
const DURATION = 1400;

const parse = (value) => {
  const m = String(value).match(/^(\D*)([\d\s\u202f\u00a0.]+)(.*)$/);
  if (!m) return null;
  return { prefix: m[1], target: parseInt(m[2].replace(/\D/g, ""), 10), suffix: m[3] };
};

const format = (n) => String(n).replace(/\B(?=(\d{3})+(?!\d))/g, " ");

const Counter = ({ value, start, testId }) => {
  const parsed = parse(value);
  const [n, setN] = useState(0);
  useEffect(() => {
    if (!start || !parsed) return;
    let raf;
    const t0 = performance.now();
    const tick = (now) => {
      const p = Math.min((now - t0) / DURATION, 1);
      setN(Math.round(parsed.target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [start]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!parsed) return <span data-testid={testId}>{value}</span>;
  return (
    <span data-testid={testId}>
      {parsed.prefix}{format(n)}{parsed.suffix}
    </span>
  );
};

export const StatsSection = ({ stats }) => {
  const ref = useRef(null);
  const [start, setStart] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setStart(true);
          obs.disconnect();
        }
      },
      { threshold: 0.35 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);

  return (
    <section className="px-5 py-20 lg:px-8 lg:py-28" data-testid="stats-section">
      <div
        ref={ref}
        className="mx-auto max-w-7xl rounded-[28px] border border-[#C9A84C]/40 bg-[#F3EAD3] px-6 py-14 shadow-[0_30px_60px_-40px_rgba(44,44,44,0.35)] sm:px-10 lg:px-14 lg:py-20"
      >
        <div className="mb-12 flex flex-col items-center text-center">
          <Sparkles size={22} strokeWidth={1.25} className="mb-4 text-[#C9A84C]" />
          <p className="text-sm uppercase tracking-[0.25em] text-[#9A7B2C]">Afro Regal en chiffres</p>
          <h2 className="mt-3 font-serif text-3xl text-[#2C2C2C] lg:text-4xl">Des moments partagés, par milliers</h2>
        </div>
        <div className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-5 lg:gap-5">
          {stats.map((s, i) => {
            const Icon = ICONS[i % ICONS.length];
            return (
              <div
                key={i}
                data-testid={`stat-card-${i}`}
                className="group flex flex-col items-center rounded-2xl border border-[#C9A84C]/50 bg-white px-5 py-10 text-center transition-[transform,box-shadow] duration-300 hover:-translate-y-1 hover:shadow-[0_20px_40px_-24px_rgba(201,168,76,0.6)]"
              >
                <span className="mb-6 flex h-14 w-14 items-center justify-center rounded-full border border-[#C9A84C]/50 text-[#C9A84C] transition-colors duration-300 group-hover:bg-[#C9A84C] group-hover:text-white">
                  <Icon size={24} strokeWidth={1.25} />
                </span>
                <p className="whitespace-nowrap font-serif text-4xl font-600 leading-none text-[#C9A84C] lg:text-[2.6rem] xl:text-5xl">
                  <Counter value={s.value} start={start} testId={`stat-value-${i}`} />
                </p>
                <span className="my-5 h-px w-10 bg-[#C9A84C]/50" />
                <p className="text-base leading-snug text-[#2C2C2C]" data-testid={`stat-label-${i}`}>{s.label}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};
